import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterOption } from '@/types/filters.types';
import * as adminSvc from '@/services/admin.service';
import type { AdminUser } from '@/services/admin.service';
import { MEMBERSHIP_STATUS_OPTIONS } from '@/pages/memberships/membershipsManagementShared';
import { MEMBER_STATUS_OPTIONS } from '@/lib/filterRegistry';
import { buildLeadSourceOptions } from '@/hooks/filters/leadSourceOptions';

export function usePatientFilterOptions(opts?: { enabled?: boolean }): Record<string, FilterOption[]> {
  const enabled = opts?.enabled ?? true;

  const { data: leadSourceCategories } = useQuery({
    queryKey: ['lead-source-categories'],
    queryFn: adminSvc.listLeadSourceCategories,
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: users } = useQuery({
    queryKey: ['admin-users'],
    queryFn: adminSvc.listUsers,
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: tagsCustom } = useQuery({
    queryKey: ['tags-custom-active'],
    queryFn: adminSvc.listTagsCustom,
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: facilities } = useQuery({
    queryKey: ['admin-facilities'],
    queryFn: adminSvc.listFacilities,
    staleTime: 5 * 60_000,
    enabled,
  });

  return useMemo<Record<string, FilterOption[]>>(() => {
    const leadSourceOptions: FilterOption[] = buildLeadSourceOptions(leadSourceCategories);

    const coordinatorOptions = (users ?? [])
      .filter((u: AdminUser) => u.active && u.role && u.role.toLowerCase().includes('coordinator'))
      .map((u: AdminUser) => ({ value: String(u.userId), label: `${u.firstName} ${u.lastName}`.trim() || 'Unknown' }));

    const tagOptions = (tagsCustom ?? [])
      .map((t) => ({ value: String(t.tagsCustomID), label: t.title || 'Unnamed Tag' }));

    const facilityOptions = (facilities ?? []).filter((f) => f.active).map((f) => ({
      value: String(f.facilityId),
      label: f.facilityName || 'Unknown',
    }));

    return {
      'membership-status': MEMBERSHIP_STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
      'member-status': MEMBER_STATUS_OPTIONS,
      'lead-source': leadSourceOptions,
      coordinator: coordinatorOptions,
      tags: tagOptions,
      facility: facilityOptions,
    };
  }, [leadSourceCategories, users, tagsCustom, facilities]);
}
